import { DeleteOutlined } from '@ant-design/icons'
import { simpleNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { CustomSelectEditor } from '@components/_SiteWide/Grid/CellEditors/CustomSelectEditor'
import { NumberCellEditor } from '@components/_SiteWide/Grid/CellEditors/NumberCellEditor'
import { CustomHeaderTooltips } from '@components/_SiteWide/Grid/CustomHeaderTooltips'
import { isEmptyPinnedCell, numberComparator, stringComparator } from '@components/_SiteWide/Grid/dirtyEditUtil'
import { addCommasToNumber, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import type { ColDef } from 'ag-grid-community'
import { Popconfirm, Tooltip } from 'antd'
import React from 'react'

export const getColumnDefs = (handleDelete, hasEdits): ColDef[] => {
  return [
    {
      headerName: 'Credential Name',
      field: 'credential_name',
      editable: true,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 24,
      },
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips displayValue={() => <Texto>List Driver Credentials</Texto>} />
      ),
      cellRenderer: (params) => {
        const empty = isEmptyPinnedCell(params, 'credential_name')
        return <Texto style={empty && { color: 'var(--gray-500)' }}>{empty ? '-- Enter --' : params.value}</Texto>
      },
      comparator: stringComparator,
    },
    {
      headerName: 'Days Valid',
      field: 'days_valid',
      editable: true,
      cellEditor: NumberCellEditor,
      cellEditorParams: {
        accessor: 'days_valid',
        minValue: 0,
      },
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips
          displayValue={() => <Texto>Days after driver certification date before expiration</Texto>}
        />
      ),
      ...simpleNumberDef('days_valid', 0),
      cellRenderer: (params) => {
        const isEmpty = isEmptyPinnedCell(params, 'days_valid')
        const isLight = params.data.dispatch_impact === 'Informational (No Impact)'
        return (
          <Texto style={{ color: isLight || isEmpty ? 'var(--gray-500)' : 'inherit' }} align='right'>
            {isEmpty ? '-- Enter --' : params.value ? addCommasToNumber(params.value, 0) : ''}
          </Texto>
        )
      },
      comparator: numberComparator,
    },
    {
      headerName: 'Warning Threshold',
      field: 'warning_threshold',
      editable: true,
      ...simpleNumberDef('warning_threshold', 0),
      cellEditor: NumberCellEditor,
      cellEditorParams: {
        accessor: 'warning_threshold',
        minValue: 0,
      },
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips displayValue={() => <Texto>Days before expiration to trigger a warning</Texto>} />
      ),
      cellRenderer: (params) => {
        const isEmpty = isEmptyPinnedCell(params, 'warning_threshold')
        const isLight = params.data.dispatch_impact === 'Informational (No Impact)'

        return (
          <Texto style={{ color: isLight || isEmpty ? 'var(--gray-500)' : 'inherit' }} align='right'>
            {isEmpty ? '-- Enter --' : params.value ? addCommasToNumber(params.value, 0) : ''}
          </Texto>
        )
      },
      comparator: numberComparator,
    },
    {
      field: 'dispatch_impact',
      headerName: 'Dispatch Impact',
      editable: true,
      minWidth: 220,
      cellEditor: CustomSelectEditor,
      cellEditorParams: {
        options: [
          {
            value: 'Alert',
            label: (
              <Horizontal style={{ backgroundColor: 'var(--theme-warning-dim)' }}>
                <Texto appearance='warning'>Alert</Texto>
              </Horizontal>
            ),
            className: 'warning-dim-bg ',
          },
          {
            value: 'Alert Manual, Block Optimizer',
            label: (
              <Horizontal style={{ backgroundColor: 'var(--theme-warning-dim)' }}>
                <Texto appearance='warning'>Alert Manual, Block Optimizer</Texto>
              </Horizontal>
            ),
            className: 'warning-dim-bg',
          },
          {
            value: 'Block Dispatch',
            label: (
              <Horizontal style={{ backgroundColor: 'var(--theme-error-dim)' }}>
                <Texto appearance='error'>Block Dispatch</Texto>
              </Horizontal>
            ),
            className: 'error-dim-bg',
          },
          {
            value: 'Informational (No Impact)',
            label: (
              <Horizontal style={{ backgroundColor: 'var(--theme-color-2-dim)' }} className='full-height-width'>
                <Texto appearance='secondary'>Informational (No Impact)</Texto>
              </Horizontal>
            ),
            className: 'theme-2-bg',
          },
        ],
        filterOption: (input, option) => option.value.toLowerCase().includes(input?.toLowerCase()),
        mode: 'single',
        accessor: 'dispatch_impact',
      },
      cellRenderer: (params) => {
        const empty = isEmptyPinnedCell(params, 'dispatch_impact')
        return <Texto style={empty && { color: 'var(--gray-500)' }}>{empty ? '-- Select --' : params.value}</Texto>
      },
      tooltipComponent: (params) => {
        const items = [
          {
            label: 'Alert',
            description: 'Alert on Driver Dispatch Driver Card and can dispatch',
          },
          {
            label: 'Alert Manual, Block Optimizer',
            description: `Alert on Driver Dispatch Driver Card of manually dispatched orders but blocks auto dispatching by Route Optimizer.  Missing card or certification date will alert dispatch.`,
          },
          {
            label: 'Block',
            description: 'Blocks dispatch with error message',
          },
          {
            label: 'Informational (No Impact):',
            description: 'No alert or blocking dispatch',
          },
        ]
        return (
          <CustomHeaderTooltips
            displayValue={() => (
              <>
                <Texto className='mb-1'>
                  If orders or backhauls are attempted to be dispatched to a driver with no or an expired card or
                  credential then:
                </Texto>
                {items.map((item) => (
                  <Horizontal key={item.label} className='mb-1'>
                    <Texto style={{ textWrap: 'nowrap' }} className='mr-2'>
                      {item.label}:
                    </Texto>
                    <Texto>{item.description}</Texto>
                  </Horizontal>
                ))}
              </>
            )}
          />
        )
      },
      headerTooltip: 'true',
      cellStyle: (params) => {
        const dispatchImpact = params.data?.dispatch_impact
        const bgColor =
          dispatchImpact === 'Block Dispatch'
            ? 'var(--theme-error-dim)'
            : dispatchImpact === 'Informational (No Impact)'
              ? 'var(--theme-color-2-dim)'
              : (dispatchImpact === 'Alert Manual, Block Optimizer' || dispatchImpact === 'Alert') &&
                'var(--theme-warning-dim)'

        return { backgroundColor: bgColor || '' }
      },
      comparator: stringComparator,
    },
    {
      field: 'credential-management-actions',
      headerName: 'Actions',
      editable: false,
      sortable: false,
      filter: false,
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips
          displayValue={() => (
            <Texto>Deleting a Credential will also remove it from all drivers with this credential.</Texto>
          )}
        />
      ),
      cellRenderer: (params) => {
        if (hasEdits) {
          return (
            <Tooltip title='Please save or discard terminal changes before deleting a card'>
              <GraviButton
                icon={<DeleteOutlined style={{ color: 'var(--gray-500)' }} />}
                style={{ backgroundColor: 'var(--theme-error-dim)', borderRadius: '3px', cursor: 'not-allowed' }}
              />
            </Tooltip>
          )
        }
        return (
          <Popconfirm
            title='Deleting a Credential will remove it from all drivers. Continue with Credential Deletion?'
            onConfirm={() => handleDelete(params.data.credential_id)}
            okText='Confirm'
          >
            <GraviButton
              icon={<DeleteOutlined />}
              style={{ backgroundColor: 'var(--theme-error-dim)', borderRadius: '3px' }}
            />
          </Popconfirm>
        )
      },
    },
  ]
}
